import AppEth from '@ledgerhq/hw-app-eth';
import HookedWalletSubprovider, { IMsgData } from 'web3-provider-engine/subproviders/hooked-wallet';
import stripHexPrefix from 'strip-hex-prefix';
import { getStructHash } from 'eip-712';
import { ItxData, Transaction } from './ethereumTx';
import { showToast } from './toastService';

const defaultOptions = {
  networkId: 1, // mainnet
  paths: ["44'/60'/x'/0/0", "44'/60'/0'/x"], // ledger live derivation path
  askConfirm: false,
  accountsLength: 1,
  accountsOffset: 0,
};

export interface ICreateLedgerSubprovider {
  networkId: number
  paths: string[]
  askConfirm?: boolean
  accountsLength: number
  accountsOffset?: number
}

type TAddresses = {
  [key: string]: string
};

/**
 * Create a HookedWalletSubprovider for Ledger devices.
 * @param getTransport gets lazily called each time the device is needed. It is a function that returns a Transport instance. You can typically give `()=>TransportU2F.create()`
 * @param options
 * @example
 */
export default function createLedgerSubprovider(getTransport: () => any, options: ICreateLedgerSubprovider) {
  if (options && 'path' in options) {
    throw new Error(
      "@ledgerhq/web3-subprovider: path options was replaced by paths. example: paths: [\"44'/60'/x'/0/0\"]",
    );
  }
  const {
    networkId, paths, askConfirm, accountsLength, accountsOffset,
  } = {
    ...defaultOptions,
    ...options,
  };

  if (!paths.length) {
    throw new Error('paths must not be empty');
  }

  const addressToPathMap: TAddresses = {};
  let addresses: TAddresses = {};

  async function getLedgerAccounts() {
    const transport = await getTransport();
    addresses = {};
    try {
      const eth = new AppEth(transport);
      for (let i = accountsOffset; i < accountsOffset + accountsLength; i += 1) {
        const x = Math.floor(i / paths.length);
        const pathIndex = i - paths.length * x;
        const path = paths[pathIndex].replace('x', String(x));
        /* eslint-disable no-await-in-loop */
        const address = await eth.getAddress(path, askConfirm, false);
        /* eslint-enable no-await-in-loop */
        addresses[path] = address.address;
        addressToPathMap[address.address.toLowerCase()] = path;
      }
    } finally {
      await transport.close();
    }
    return addresses;
  }

  async function signPersonalMessage(msgData: IMsgData) {
    const path = addressToPathMap[msgData.from.toLowerCase()];
    if (!path) throw new Error(`address unknown '${ msgData.from }'`);
    const transport = await getTransport();
    let signature;
    try {
      const eth = new AppEth(transport);
      showToast('Please sign personal message with your ledger device', 5 * 1000);
      const result = await eth.signPersonalMessage(
        path,
        stripHexPrefix(msgData.data),
      );
      const v = parseInt(result.v?.toString(), 10) - 27;
      let vHex = v.toString(16);
      if (vHex.length < 2) {
        vHex = `0${v}`;
      }
      signature = `0x${result.r}${result.s}${vHex}`;
    } finally {
      await transport.close();
    }
    return signature;
  }

  async function signTypedData(typedData: IMsgData) {
    const path = addressToPathMap[typedData.from.toLowerCase()];
    if (!path) throw new Error(`address unknown '${ typedData.from }'`);

    const transport = await getTransport();
    let signature;
    try {
      const eth = new AppEth(transport);
      const data = JSON.parse(typedData.data);
      const message = getStructHash(data, data.primaryType, data.message).toString('hex');
      const domain = getStructHash(data, 'EIP712Domain', data.domain).toString('hex');
      showToast('Please sign typed data with your ledger device', 5 * 1000);
      const result = await eth.signEIP712HashedMessage(
        path,
        domain,
        message,
      );
      let v: string | number = result.v - 27;
      v = v.toString(16);
      if (v.length < 2) {
        v = `0${v}`;
      }
      signature = `0x${result.r}${result.s}${v}`;
    } finally {
      await transport.close();
    }
    return signature;
  }

  async function signTransaction(txData: ItxData) {
    const path = addressToPathMap[txData.from.toLowerCase()];
    if (!path) throw new Error(`address unknown '${ txData.from }'`);
    const transport = await getTransport();
    let signedTransaction;
    try {
      const eth = new AppEth(transport);
      const transaction = new Transaction(txData, networkId);

      showToast('Please sign transaction with your ledger device', 5 * 1000);

      const signature = await eth.signTransaction(
        path,
        transaction.getMessageToSign().toString('hex'),
      );
      transaction.sign(signature);
      transaction.validateSignature();
      signedTransaction = `0x${transaction.serialize().toString('hex')}`;

      // Apply v check for Legacy transactions
      // EIP155: v should be chain_id * 2 + {35, 36}
      // if (parseInt(txData.type, 16) !== 2) {
      //   const v = parseInt(signature.v, 10);
      //
      //   const signedChainId = Math.floor((v - 35) / 2);
      //   if (signedChainId !== networkId) {
      //     throw makeError(
      //       'Invalid networkId signature returned.' +
      //         `Expected: ${networkId}, Got: ${signedChainId}, v: ${v}`,
      //       'InvalidNetworkId',
      //     );
      //   }
      // }
    } finally {
      await transport.close();
    }
    return signedTransaction;
  }

  const subprovider = new HookedWalletSubprovider({
    getAccounts: (callback) => {
      (Object.values(addresses).length ? Promise.resolve(addresses) : getLedgerAccounts())
        .then((res) => callback(null, Object.values(res)))
        .catch((err) => callback(err, null));
    },
    signPersonalMessage: (txData, callback) => {
      signPersonalMessage(txData)
        .then((res) => callback(null, res))
        .catch((err) => callback(err, null));
    },
    signTransaction: (txData: ItxData, callback) => {
      signTransaction(txData)
        .then((res) => callback(null, res))
        .catch((err) => callback(err, null));
    },
    signTypedMessage: (msgData, callback) => {
      signTypedData(msgData)
        .then((res) => callback(null, res))
        .catch((err) => callback(err, null));
    },
  });

  return subprovider;
}
